// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// weather.ts — the schedule and the snow.
//
// THE SCHEDULE EXISTS IN FULL AT t = 0, which is what turns pausing from panic into a plan. It is
// drawn from the `weather` stream alone, so no player action can ever shift it: with one shared PRNG
// a dig designated on tick 400 consumes a draw and the blizzard on day 7 lands somewhere else, which
// nobody would ever find and which would quietly make the forecast a lie.
//
// The opening quarter is PINNED per mode rather than rolled. A free draw on day 1 dusk makes the
// first minute a coin flip between "nothing moves at all" (calm) and "the corridor severs before the
// player has understood the HUD" (storm), with no middle band — so the first quarter is always Snow:
// visibly weather, not yet a crisis.

import type { Rng } from '@ben-gy/game-engine/rng';
import {
  MAX_DEPTH,
  MIN_STORM_QUARTERS,
  PHASE_RATE,
  Phase,
  QUARTERS_PER_DAY,
  SHELTER_CAP,
  SHELTER_DIV,
  SNOW_OVERFLOW,
} from './modes';
import { COLS, ROWS, TILES, colOf, rowOf } from './map';
import { below } from './streams';

/**
 * Generate the whole winter, one entry per day-quarter.
 *
 * The shape is deliberate rather than a per-quarter roll: a storm ALWAYS runs for at least
 * MIN_STORM_QUARTERS consecutive quarters, because 400 < 450 < 800 — the measured tick counts for a
 * one-tile and a three-tile gap to sever — is the inequality the entire design rests on, and a
 * one-quarter storm (225 ticks) would sever nothing at all. Storm frequency rises through the
 * winter, so the last days are genuinely worse than the first and the forecast is worth reading
 * early rather than only reacting to.
 */
export function makeSchedule(rng: Rng, days: number): Phase[] {
  const n = days * QUARTERS_PER_DAY;
  const out: Phase[] = new Array(n).fill(Phase.Calm);
  let q = 0;
  // Day 1 dawn..dusk is pinned: weather you can see, that is not yet trying to kill you.
  while (q < Math.min(n, 3)) out[q++] = Phase.Snow;

  while (q < n) {
    // Storm pressure climbs from roughly one quarter in six to one in two across the winter.
    const progress = (q * 100) / n;
    const stormPct = 14 + ((progress * 34) / 100 | 0);
    const roll = below(rng, 100);
    if (roll < stormPct) {
      const len = MIN_STORM_QUARTERS + below(rng, 2);
      for (let i = 0; i < len && q < n; i++) out[q++] = Phase.Storm;
    } else if (roll < stormPct + 38) {
      const len = 1 + below(rng, 3);
      for (let i = 0; i < len && q < n; i++) out[q++] = Phase.Snow;
    } else {
      const len = 1 + below(rng, 2);
      for (let i = 0; i < len && q < n; i++) out[q++] = Phase.Calm;
    }
  }
  return out;
}

/** The phase in force at a tick. */
export function phaseAt(schedule: readonly Phase[], tick: number, ticksPerQuarter: number): Phase {
  const q = (tick / ticksPerQuarter) | 0;
  return schedule[Math.min(q, schedule.length - 1)] ?? Phase.Calm;
}

/**
 * What the snow needs to know about a tile, supplied by the caller so this module never imports the
 * simulation and can be driven by a twenty-line harness.
 */
export interface SnowWorld {
  /** Does snow fall here at all? False indoors, false on rock. An OPEN door's tile is outdoors. */
  accumulates(t: number): boolean;
  /**
   * Is this tile an obstruction that snow drifts against? Rock, wall, closed door, any solid
   * building. Depth-3 tiles are added by the accumulator itself.
   */
  obstructs(t: number): boolean;
  /** Path tiles take half. */
  isPath(t: number): boolean;
}

/**
 * One accumulation batch, over `depth` and `fill` in place.
 *
 * SNOW DRIFTS AGAINST OBSTRUCTIONS. `shelter` counts a tile's four neighbours that obstruct, or that
 * are already at maximum depth, and the rate is scaled by `(1 + min(shelter, 3)) / 2`. So the open
 * middle of a wide corridor takes half the nominal rate, a tile against a single wall takes exactly
 * the nominal rate, and a one-tile slot between two walls takes one and a half times it. As the
 * edges of a wide gap reach depth 3 they become obstructions themselves, so a corridor visibly
 * closes IN FROM THE SIDES — which is both the correct physical picture and the reason width buys
 * time rather than costing it.
 *
 * The batch reads a SNAPSHOT of depth, so the result does not depend on the order tiles are visited.
 * That is a determinism requirement, not a stylistic one: a shared daily seed and a headless balance
 * sim both have to reproduce a winter exactly.
 */
export function snowBatch(
  world: SnowWorld,
  depth: Uint8Array,
  fill: Int32Array,
  phase: Phase,
  scratch: Uint8Array,
): void {
  const rate = PHASE_RATE[phase];
  if (rate <= 0) return;
  scratch.set(depth);

  for (let t = 0; t < TILES; t++) {
    if (depth[t] >= MAX_DEPTH) continue;
    if (!world.accumulates(t)) continue;

    const c = colOf(t);
    const r = rowOf(t);
    let shelter = 0;
    if (r > 0 && blocks(world, scratch, t - COLS)) shelter++;
    if (r < ROWS - 1 && blocks(world, scratch, t + COLS)) shelter++;
    if (c > 0 && blocks(world, scratch, t - 1)) shelter++;
    if (c < COLS - 1 && blocks(world, scratch, t + 1)) shelter++;

    const mult = 1 + (shelter < SHELTER_CAP ? shelter : SHELTER_CAP);
    let add = ((rate * mult) / SHELTER_DIV) | 0;
    if (world.isPath(t)) add = (add / 2) | 0;
    if (add <= 0) continue;

    fill[t] += add;
    while (fill[t] >= SNOW_OVERFLOW && depth[t] < MAX_DEPTH) {
      fill[t] -= SNOW_OVERFLOW;
      depth[t]++;
    }
    if (depth[t] >= MAX_DEPTH) fill[t] = 0;
  }
}

function blocks(world: SnowWorld, snapshot: Uint8Array, t: number): boolean {
  return snapshot[t] >= MAX_DEPTH || world.obstructs(t);
}
